import genderAsset from '../../assets/SystemSettingSand/gender-icon.svg';
import React, { useState, useEffect } from "react";
import {
  Image as ImageIcon,
  Save,
  Loader,
  AlertTriangle,
  CheckCircle,
  AlertCircle,
  RotateCcw,
  KeyRound,
} from "lucide-react";
import {
  getCurrentAvatarMode,
  updateAvatarMode,
  getAvatarCompatibility,
  getProviderKey,
  getAllProviderKeys,
  saveProviderKey,
} from "../../services/settingsapi";
import ConfirmationDialog from "../ConfirmationDialog";
import {
  SettingsSection,
  InputField,
  SelectField,
} from "../SettingsUIComponents";

const AvatarSettings = ({
  canEditSettings,
  canViewSettings,
  canDeleteSettings,
}) => {
  const [avatarSettings, setAvatarSettings] = useState({
    currentMode: "did",
    currentGender: "male",
    providers: [],
    providerKeys: {},
    selectedProvider: "",
    providerApiKey: "",
    editingProvider: null,
  });

  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarMessage, setAvatarMessage] = useState({ type: "", text: "" });
  const [avatarCompatibility, setAvatarCompatibility] = useState(null);

  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  // Fetch avatar settings on component mount
  useEffect(() => {
    if (!canViewSettings) return;

    const fetchAvatarSettings = async () => {
      try {
        setAvatarLoading(true);
        const currentMode = await getCurrentAvatarMode();
        const allKeys = await getAllProviderKeys();

        const providerKeysData = {};
        if (allKeys.providers && Array.isArray(allKeys.providers)) {
          for (const provider of allKeys.providers) {
            try {
              const keyData = await getProviderKey(provider.provider);
              providerKeysData[provider.provider] = keyData;
            } catch (error) {
              console.warn(
                `Could not fetch key for ${provider.provider}:`,
                error,
              );
            }
          }
        }

        let compatibility = null;
        try {
          compatibility = await getAvatarCompatibility();
          setAvatarCompatibility(compatibility);
        } catch (compatibilityError) {
          console.warn(
            "Could not load avatar compatibility:",
            compatibilityError,
          );
        }
        setAvatarSettings((prevSettings) => ({
          ...prevSettings,
          currentMode: currentMode.avatar_mode || "did",
          currentGender:
            compatibility?.avatar_gender || currentMode.avatar_gender || "male",
          providers: allKeys.providers || [],
          providerKeys: providerKeysData,
        }));
        setAvatarMessage({ type: "", text: "" });
      } catch (error) {
        console.error("Error fetching avatar settings:", error);
        setAvatarMessage({
          type: "error",
          text: "Error loading avatar settings: " + error.message,
        });
      } finally {
        setAvatarLoading(false);
      }
    };

    fetchAvatarSettings();
  }, [canViewSettings]);

  // Save avatar mode
  const handleSaveMode = async () => {
    if (!canEditSettings) {
      setAvatarMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    setConfirmationDialog({
      open: true,
      title: "Confirm Avatar Mode",
      message: `Are you sure you want to change the avatar mode to "${avatarSettings.currentMode}"? This will affect how avatars are displayed in interviews.`,
      onConfirm: async () => {
        try {
          setAvatarLoading(true);
          await updateAvatarMode({
            mode: avatarSettings.currentMode,
            gender: avatarSettings.currentGender,
          });
          setAvatarMessage({
            type: "success",
            text: "Avatar mode updated successfully!",
          });
          setTimeout(() => setAvatarMessage({ type: "", text: "" }), 5000);
        } catch (error) {
          console.error("Error updating avatar mode:", error);
          setAvatarMessage({
            type: "error",
            text: "Error updating avatar mode: " + error.message,
          });
        } finally {
          setAvatarLoading(false);
        }
      },
    });
  };

  const handleGenderChange = async (gender) => {
    if (!canEditSettings || gender === avatarSettings.currentGender) return;

    try {
      setAvatarLoading(true);

      const modeToSave = avatarSettings.currentMode;

      // Check current provider before changing avatar gender.
      const currentCompatibility = await getAvatarCompatibility();

      // Legacy uses browser TTS, so Male avatar is the default.
      // Do not silently allow Female for Legacy.
      if (currentCompatibility?.provider === "legacy" && gender === "female") {
        setConfirmationDialog({
          open: true,
          title: "Legacy Interview Provider Selected",
          message:
            "The current interview provider is Legacy, which uses the browser's default voice to read interview questions. For Legacy interviews, the Male avatar is used by default. To use a Female avatar with a gender-specific voice, please switch the interview provider to Gemini Live or OpenAI TTS.",
          onConfirm: () => {
            setConfirmationDialog((current) => ({
              ...current,
              open: false,
            }));
          },
        });

        return;
      }

      const saved = await updateAvatarMode({
        mode: modeToSave,
        gender,
      });

      setAvatarSettings((current) => ({
        ...current,
        currentMode: saved?.avatar_mode || modeToSave,
        currentGender: saved?.avatar_gender || gender,
      }));

      const compatibility = await getAvatarCompatibility();
      setAvatarCompatibility(compatibility);

      const savedGender = saved?.avatar_gender || gender;

      setAvatarMessage({
        type: "success",
        text: `${
          savedGender === "female" ? "Female" : "Male"
        } ${modeToSave === "2d" ? "2D" : "static"} avatar selected.`,
      });

      if (compatibility.compatible === false) {
        const voiceSourceLabel =
          compatibility.voice_source === "openai_tts"
            ? "OpenAI TTS"
            : "Gemini Live";

        const avatarLabel =
          compatibility.avatar_mode === "2d" ? "2D avatar" : "static avatar";

        setConfirmationDialog({
          open: true,
          title: "Voice & Avatar Mismatch",
          message: `Your ${voiceSourceLabel} voice is ${compatibility.voice_gender}, while the ${avatarLabel} is ${compatibility.avatar_gender}. Your choice has been saved. Switch the avatar back to ${compatibility.voice_gender} for a consistent interviewer experience?`,
          onConfirm: () => handleGenderChange(compatibility.voice_gender),
        });
      }
    } catch (error) {
      console.error("Error updating avatar gender:", error);

      setAvatarMessage({
        type: "error",
        text: "Error saving avatar gender: " + error.message,
      });
    } finally {
      setAvatarLoading(false);
    }
  };

  // Save provider key
  const handleSaveProviderKey = async () => {
    if (!canEditSettings) {
      setAvatarMessage({
        type: "error",
        text: "You do not have permission to edit settings",
      });
      return;
    }

    if (
      !avatarSettings.selectedProvider ||
      !avatarSettings.providerApiKey.trim()
    ) {
      setAvatarMessage({
        type: "error",
        text: "Please select a provider and enter an API key",
      });
      return;
    }

    try {
      setAvatarLoading(true);
      const response = await saveProviderKey({
        provider: avatarSettings.selectedProvider,
        api_key: avatarSettings.providerApiKey,
      });

      const allKeys = await getAllProviderKeys();
      const providerKeysData = {};
      if (allKeys.providers && Array.isArray(allKeys.providers)) {
        for (const provider of allKeys.providers) {
          try {
            const keyData = await getProviderKey(provider.provider);
            providerKeysData[provider.provider] = keyData;
          } catch (error) {
            console.warn(
              `Could not fetch key for ${provider.provider}:`,
              error,
            );
          }
        }
      }

      setAvatarSettings((prevSettings) => ({
        ...prevSettings,
        providers: allKeys.providers || [],
        providerKeys: providerKeysData,
        selectedProvider: "",
        providerApiKey: "",
        editingProvider: null,
      }));

      setAvatarMessage({
        type: "success",
        text: response.message || "Provider API key saved successfully",
      });
      setTimeout(() => setAvatarMessage({ type: "", text: "" }), 5000);
    } catch (error) {
      console.error("Error saving provider key:", error);
      setAvatarMessage({
        type: "error",
        text: "Error saving provider key: " + error.message,
      });
    } finally {
      setAvatarLoading(false);
    }
  };

  return (
    <>
      <SettingsSection
        icon={ImageIcon}
        title="Avatar Configuration"
        description="Manage avatar mode and provider API keys"
      >
        <div className="space-y-6">
          {avatarMessage.text && (
            <div
              className={`p-4 rounded-xl border ${
                avatarMessage.type === "success"
                  ? "bg-green-50 border-green-300 text-green-700"
                  : "bg-red-50 border-red-300 text-red-700"
              }`}
            >
              <p className="text-sm font-semibold flex items-center gap-2">
                {avatarMessage.type === "success" ? (
                  <CheckCircle className="w-4 h-4" />
                ) : (
                  <AlertCircle className="w-4 h-4" />
                )}
                {avatarMessage.text}
              </p>
            </div>
          )}

          {avatarLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 text-teal-700 animate-spin" />
            </div>
          ) : (
            <>
              {/* Avatar Mode Selection */}
              <div className="settings-avatar-mode">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Avatar Mode Selection
                </h3>
                <SelectField
                  label="Select Avatar Mode"
                  value={avatarSettings.currentMode}
                  onChange={(e) => {
                    setAvatarSettings((prevSettings) => ({
                      ...prevSettings,
                      currentMode: e.target.value,
                    }));
                  }}
                  options={[
                    { value: "did", label: "DID (D-ID)" },
                    { value: "heygen", label: "HeyGen" },
                    { value: "static", label: "VLink Avatar (S)" },
                    { value: "custom", label: "VLink Avatar (C)" },
                    { value: "2d", label: "2D Avatar" },
                  ]}
                  helpText="Select the primary avatar provider mode for your application"
                  canEdit={canEditSettings}
                />
                <div className="mt-4 flex gap-3">
                  <button
                    onClick={handleSaveMode}
                    disabled={!canEditSettings || avatarLoading}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-lg font-semibold text-sm shadow-md transition-all ${
                      canEditSettings && !avatarLoading
                        ? "bg-gradient-to-r from-teal-700 to-teal-600 text-white hover:shadow-lg hover:from-teal-800 hover:to-teal-700"
                        : "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                    }`}
                    title={
                      !canEditSettings
                        ? "You do not have permission to edit settings"
                        : ""
                    }
                  >
                    <Save className="w-4 h-4" />
                    Save Mode
                  </button>
                </div>
                {(avatarSettings.currentMode === "static" ||
                  avatarSettings.currentMode === "2d") && (
                  <div className="settings-avatar-gender">
                    <h4 className="text-base font-semibold text-gray-900">
                      {avatarSettings.currentMode === "2d"
                        ? "2D Avatar Gender"
                        : "Static Avatar Gender"}
                    </h4>{" "}
                    <p className="text-sm text-gray-600 mt-1">
                      Choose the video avatar used in new static interviews. An
                      interview already in progress keeps its current video
                      buffers.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                      {[
                        { id: "male", icon: "👨", label: "Male" },
                        { id: "female", icon: "👩", label: "Female" },
                      ].map((option) => {
                        const selected =
                          avatarSettings.currentGender === option.id;
                        return (
                          <button
                            key={option.id}
                            type="button"
                            onClick={() => handleGenderChange(option.id)}
                            disabled={!canEditSettings || avatarLoading}
                            className={`p-4 text-left rounded-xl border-2 transition-all focus:outline-none focus:ring-2 focus:ring-teal-400 disabled:opacity-60 ${selected ? "border-teal-600 bg-teal-100" : "border-teal-200 bg-white hover:border-teal-400"}`}
                            aria-pressed={selected}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-gray-900">
                                <img className="settings-gender-icon" src={genderAsset} alt="" /> {option.label}
                              </span>
                              {selected && (
                                <span className="text-xs font-bold text-teal-700">
                                  ✓ Selected
                                </span>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    <p className="text-sm font-medium text-teal-800 mt-3">
                      {avatarSettings.currentGender === "female"
                        ? "Female"
                        : "Male"}{" "}
                      avatar selected
                    </p>
                  </div>
                )}
                {avatarCompatibility?.compatible === false && (
                  <div className="mt-5 p-4 rounded-xl border border-amber-300 bg-amber-50 text-amber-900">
                    <p className="font-semibold">⚠ Voice & Avatar mismatch</p>
                    <p className="text-sm mt-1">
                      {avatarCompatibility.voice_source === "openai_tts"
                        ? "OpenAI TTS"
                        : "Gemini Live"}{" "}
                      voice: {avatarCompatibility.voice_gender}.{" "}
                      {avatarCompatibility.avatar_mode === "2d"
                        ? "2D avatar"
                        : "Static avatar"}
                      : {avatarCompatibility.avatar_gender}.
                    </p>{" "}
                    <button
                      type="button"
                      onClick={() =>
                        handleGenderChange(avatarCompatibility.voice_gender)
                      }
                      disabled={!canEditSettings || avatarLoading}
                      className="mt-3 text-sm font-semibold underline hover:no-underline"
                    >
                      Switch avatar to {avatarCompatibility.voice_gender}
                    </button>
                  </div>
                )}
              </div>

              {/* Provider API Key Form */}
              <div className="settings-avatar-keys">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Provider API Keys
                </h3>

                <div className="space-y-4">
                  <SelectField
                    label="Select Provider"
                    value={avatarSettings.selectedProvider}
                    onChange={(e) => {
                      const selectedProvider = e.target.value;
                      setAvatarSettings((prevSettings) => ({
                        ...prevSettings,
                        selectedProvider: selectedProvider,
                        providerApiKey:
                          selectedProvider &&
                          avatarSettings.providerKeys[selectedProvider]
                            ? avatarSettings.providerKeys[selectedProvider]
                                .api_key
                            : "",
                      }));
                    }}
                    options={[
                      { value: "", label: "Choose a provider..." },
                      { value: "did", label: "DID (D-ID)" },
                      { value: "heygen", label: "HeyGen" },
                      { value: "static", label: "VLink Avatar (S)" },
                      { value: "custom", label: "VLink Avatar (C)" },
                    ]}
                    helpText="Select a provider to add or update its API key"
                    canEdit={canEditSettings}
                  />

                  {avatarSettings.selectedProvider && (
                    <>
                      <InputField
                        label={`${avatarSettings.selectedProvider.toUpperCase()} API Key`}
                        type="text"
                        value={avatarSettings.providerApiKey}
                        onChange={(e) => {
                          setAvatarSettings((prevSettings) => ({
                            ...prevSettings,
                            providerApiKey: e.target.value,
                          }));
                        }}
                        placeholder={`Enter ${avatarSettings.selectedProvider} API key`}
                        helpText={
                          avatarSettings.providerKeys[
                            avatarSettings.selectedProvider
                          ]
                            ? "Update the API key for this provider"
                            : "Enter a new API key for this provider"
                        }
                        canEdit={canEditSettings}
                        leadingIcon={KeyRound}
                      />

                      {avatarSettings.providerKeys[
                        avatarSettings.selectedProvider
                      ] && (
                        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-xs text-green-700 font-semibold mb-2">
                            Current API Key (selectable):
                          </p>
                          <div className="p-3 bg-white rounded border border-green-300 font-mono text-xs text-gray-700 break-all select-all">
                            {
                              avatarSettings.providerKeys[
                                avatarSettings.selectedProvider
                              ].api_key
                            }
                          </div>
                        </div>
                      )}

                      <button
                        onClick={handleSaveProviderKey}
                        disabled={
                          !canEditSettings ||
                          !avatarSettings.providerApiKey.trim() ||
                          avatarLoading
                        }
                        className={`w-full flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-semibold shadow-md transition-all ${
                          canEditSettings &&
                          avatarSettings.providerApiKey.trim() &&
                          !avatarLoading
                            ? "bg-gradient-to-r from-teal-700 to-teal-600 text-white hover:shadow-lg hover:from-teal-800 hover:to-teal-700"
                            : "bg-gray-300 text-gray-500 cursor-not-allowed opacity-50"
                        }`}
                        title={
                          !canEditSettings
                            ? "You do not have permission to edit settings"
                            : ""
                        }
                      >
                        {avatarLoading ? (
                          <>
                            <Loader className="w-5 h-5 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-5 h-5" />
                            Save API Key
                          </>
                        )}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </SettingsSection>

      <ConfirmationDialog
        open={confirmationDialog.open}
        onClose={() =>
          setConfirmationDialog({ ...confirmationDialog, open: false })
        }
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        confirmText="Confirm"
        cancelText="Cancel"
        isDangerous={false}
      />
    </>
  );
};

export default AvatarSettings;
